import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchAssignees, fetchConversations, fetchInboxCounts, fetchInboxNumbers, fetchInboxTemplates, fetchThread, fetchConversation, type InboxSegment, type ListQuery, type PageKey } from "../../api/inbox";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { inboxAccess } from "./access";

export type InboxFilter = "all" | "unread" | "mine" | "unassigned";
export const SEGMENTS: readonly InboxSegment[] = ["lead", "member", "staff"];
export const FILTERS: readonly InboxFilter[] = ["all", "unread", "mine", "unassigned"];

export interface InboxParams { seg: InboxSegment; num: string | null; filter: InboxFilter; q: string; archived: boolean }

/** Segment, number, chip, search and the archive view live in the URL query, so back and refresh keep them. */
export function useInboxParams() {
  const [sp, setSp] = useSearchParams();
  const seg = SEGMENTS.find((x) => x === sp.get("seg")) ?? "lead";
  const filter = FILTERS.find((x) => x === sp.get("f")) ?? "all";
  const params: InboxParams = { seg, num: sp.get("num"), filter, q: sp.get("q") ?? "", archived: sp.get("arch") === "1" };
  const set = (patch: Partial<InboxParams>) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      const merged = { ...params, ...patch };
      const put = (k: string, v: string | null) => { if (v) next.set(k, v); else next.delete(k); };
      put("seg", merged.seg === "lead" ? null : merged.seg);
      put("num", merged.num);
      put("f", merged.filter === "all" ? null : merged.filter);
      put("q", merged.q || null);
      put("arch", merged.archived ? "1" : null);
      return next;
    }, { replace: true });
  };
  return { params, set, search: sp.toString() ? `?${sp.toString()}` : "" };
}

/** Replies and delivery ticks reach an open thread with no push yet; stop once a read fails (404 until inbox-api is live). */
const POLL = (q: { state: { error: unknown } }) => (q.state.error ? false : 60_000);

/** Business + branch in every key: a cached list can never be read in another context (tenant guard). */
function useScopeKey() {
  const s = useSessionSnapshot();
  return [s.businessId, s.branchId] as const;
}

export function useInboxAccess() {
  const ctx = useAppContext();
  return { ctx, ...inboxAccess(ctx.has) };
}

export function useNumbers() {
  const { client } = useServices();
  const scope = useScopeKey();
  const { canView } = useInboxAccess();
  return useQuery({ queryKey: ["inbox", "numbers", ...scope], queryFn: () => fetchInboxNumbers(client), enabled: canView, retry: false, staleTime: 5 * 60_000 });
}

export function useCounts(brandId?: string | null) {
  const { client } = useServices();
  const scope = useScopeKey();
  const { canView } = useInboxAccess();
  return useQuery({ queryKey: ["inbox", "counts", ...scope, brandId ?? "all"], queryFn: () => fetchInboxCounts(client, brandId ?? undefined), enabled: canView, retry: false, refetchInterval: 60_000 });
}

/** The tab badge: unread chats across the three segments. Hidden (0) while the routes are not connected. */
export function useInboxUnread(): number {
  const q = useCounts(null);
  return q.data ? q.data.lead.unread + q.data.member.unread + q.data.staff.unread : 0;
}

export function listQueryOf(p: InboxParams, term: string): ListQuery {
  return {
    kind: p.seg,
    brandId: p.num ?? undefined,
    assignee: p.filter === "mine" ? "me" : p.filter === "unassigned" ? "unassigned" : undefined,
    unread: p.filter === "unread" || undefined,
    archived: p.archived || undefined,
    q: term || undefined,
  };
}

export function useConversationList(p: InboxParams, term: string) {
  const { client } = useServices();
  const scope = useScopeKey();
  const { canView } = useInboxAccess();
  const base = listQueryOf(p, term);
  return useInfiniteQuery({
    queryKey: ["inbox", "list", ...scope, base],
    queryFn: ({ pageParam }) => fetchConversations(client, { ...base, ...(pageParam ?? {}) }),
    initialPageParam: null as { before: number; beforeId: string } | null,
    getNextPageParam: (last) => last.next,
    enabled: canView,
    retry: false,
  });
}

export function useConversation(id: string | undefined) {
  const { client } = useServices();
  const scope = useScopeKey();
  return useQuery({ queryKey: ["inbox", "conversation", ...scope, id], queryFn: () => fetchConversation(client, id!), enabled: !!id, retry: false, refetchInterval: POLL });
}
export function useThread(id: string | undefined) {
  const { client } = useServices();
  const scope = useScopeKey();
  return useInfiniteQuery({
    queryKey: ["inbox", "thread", ...scope, id],
    queryFn: ({ pageParam }) => fetchThread(client, id!, pageParam),
    initialPageParam: null as PageKey | null,
    getNextPageParam: (last) => last.next ?? null,
    enabled: !!id,
    retry: false,
    refetchInterval: POLL,
  });
}
export function useAssignees(enabled: boolean, conversationId?: string) {
  const { client } = useServices();
  const scope = useScopeKey();
  return useQuery({ queryKey: ["inbox", "assignees", ...scope, conversationId ?? "all"], queryFn: () => fetchAssignees(client, conversationId), enabled, retry: false, staleTime: 5 * 60_000 });
}
export function useTemplates(enabled: boolean, brandId?: string) {
  const { client } = useServices();
  const scope = useScopeKey();
  return useQuery({ queryKey: ["inbox", "templates", ...scope, brandId ?? "all"], queryFn: () => fetchInboxTemplates(client, brandId), enabled, retry: false, staleTime: 5 * 60_000 });
}

/** Re-renders every minute so "Nh to reply" and the composer follow the clock without a refetch. */
export function useNow(intervalMs = 60_000): number {
  const { now } = useServices();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now();
}

export function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}
